import _ from "lodash";
import { useEffect, useRef, useState } from "react";
import { ListFieldsType, myDocumentsListName } from "src/api/config";
import { FetchOptions, getItemsRequest } from "src/modules/spItems/api";
import { modifiedItemsState } from "src/modules/spItems/selectors";
import { keyMap } from "src/pages/DashboardPage/acadia/data/keyMap";
import { FiltersStateType, Keys, KeysList, RestKeys, UserKeys } from "src/pages/DashboardPage/acadia/type";
import { UserListItem } from "src/types/main";

export function documentFiltersOptions(filters: FiltersStateType) {
  let baseFilters = _.pick(filters, ["collections", "departments", "sectors", "processes", "types"]);
  let restFilters = _.pick(filters, [
    "critical",
    "lines",
    "sap",
    "typeEquipment",
    "model",
    "manufacturer",
    "titleEquipment",
  ]);
  let userFilters = _.pick(filters, ["created", "responsibleForRevision"]);
  let dateFilters = _.pick(filters, ["dateForRevision"]);

  const configureBaseFilters: Record<Keys, number[]> = _.transform(baseFilters, (result, value, key) => {
    let newKey = keyMap[key as KeysList];
    let newValue = value.map((v) => v.id);

    if (newValue.length > 0) {
      (result as any)[newKey] = newValue;
    }
  });

  const userFiltersData = Object.keys(userFilters).reduce((acc, key) => {
    const newKey = keyMap[key as UserKeys];

    if (userFilters[key as UserKeys].length > 0) {
      const ids = userFilters[key as UserKeys].map((r: UserListItem) => r.email.toLowerCase());
      acc[newKey] = ids;
    }

    return acc;
  }, {} as Record<(typeof keyMap)[UserKeys], string[]>);

  const allFilters = Object.keys(restFilters).reduce(
    (acc, key) => {
      const newValue = restFilters[key as RestKeys];
      const newKey = keyMap[key as RestKeys];

      if (key === "critical") {
        if (newValue.length > 0 && newValue.length < 2) {
          acc[newKey] = Boolean(newValue[0].id);
        }
      } else {
        if (newValue.length > 0) {
          const arrayIds = newValue.map((r) => r.id);
          if (acc[newKey]) {
            acc[newKey] = _.uniq([...acc[newKey], ...arrayIds]);
          } else {
            acc[newKey] = newValue.map((r) => r.id);
          }
        }
      }

      return acc;
    },
    // Если запущена локалка в таблице будут выводится все документы
    { ...configureBaseFilters, ...userFiltersData, Status: [8, 3] } as Record<string, any>
  );

  if (
    dateFilters.dateForRevision.required ||
    dateFilters.dateForRevision.date.start ||
    dateFilters.dateForRevision.date.end
  ) {
    let dateFiltersData: { start: Date | null; end: Date | null }[] = [];
    const newKey = keyMap["dateForRevision"];

    if (dateFilters.dateForRevision.required) {
      allFilters["Status"] = [3];

      // dateFiltersData.push({ start: new Date(), end: moment(new Date()).add(30, "day").toDate() });
    }

    if (dateFilters.dateForRevision.date.start || dateFilters.dateForRevision.date.end) {
      dateFiltersData.push({
        start:
          typeof dateFilters.dateForRevision.date.start === "string"
            ? new Date(dateFilters.dateForRevision.date.start)
            : dateFilters.dateForRevision.date.start,
        end:
          typeof dateFilters.dateForRevision.date.end === "string"
            ? new Date(dateFilters.dateForRevision.date.end)
            : dateFilters.dateForRevision.date.end,
      });

      allFilters[newKey] = dateFiltersData;
    }
  }

  return allFilters;
}

export function useGetDocuments(filters: FiltersStateType, limit: number = 500, trigger: boolean = true) {
  const [documents, setDocuments] = useState<ListFieldsType[typeof myDocumentsListName][]>([]);
  const [downloading, setDownloading] = useState(true);
  const nextPageRef = useRef<SP.ListItemCollectionPosition | null>(null); // Для отслеживания страницы пагинации

  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        while (downloading) {
          const requestProps: FetchOptions<typeof myDocumentsListName> = {
            listName: myDocumentsListName,
            rowLimit: limit, // Количество записей за один запрос
            nextPage: nextPageRef.current!,
          };

          if (filters) {
            requestProps.filters = documentFiltersOptions(filters); // Применение фильтров, если они переданы
          }

          const response = await getItemsRequest(requestProps); // Выполнение запроса на получение данных

          const chunkDocuments = modifiedItemsState(response.items, myDocumentsListName); // Обработка полученных данных
          const nextPage = response.nextPage; // Сохранение указателя на следующую страницу

          setDocuments((prev) => [...prev, ...(chunkDocuments as [])]); // Добавление новых документов в массив

          if (chunkDocuments.length < limit) {
            setDownloading(false); // Если количество загруженных записей меньше лимита, прекращаем загрузку
            break; // Останавливаем цикл
          } else {
            nextPageRef.current = nextPage; // Переход к следующей странице
          }
        }
      } catch (error) {
        console.error("Error fetching documents: ", error);
      } finally {
        setDownloading(false);
      }
    };

    if (trigger && downloading) fetchDocuments(); // Запуск функции для получения документов

    return () => setDownloading(false); // Остановка загрузки при размонтировании компонента
  }, [downloading, trigger, JSON.stringify(filters)]);

  // Обновление состояния downloading при изменении фильтров
  useEffect(() => {
    if (trigger) {
      setDocuments([]); // Очищаем список документов при изменении фильтров
      setDownloading(true); // Запускаем загрузку заново
      nextPageRef.current = null; // Сбрасываем пагинацию
    }
  }, [JSON.stringify(filters)]); // Отслеживаем изменения фильтров

  return { documents, downloading };
}
